/**
 * Sprint 23.6 — SparkleRegen.
 *
 * The post-hire "regenerate this field via AI" trigger (Q16). Click → open the
 * right-rail chat with a composition scope pre-set to refine the current agent
 * and a pre-filled prompt addressing the named field. The user can edit the
 * prompt before sending — control intact, friction down.
 *
 * Sprint 22 owns the chat rail (ChatRailContext); Sprint 23.5 owns the
 * `composition` scope semantics + `update_composition_draft` tool-call
 * interception. SparkleRegen is the bridge: it composes the right scope +
 * prompt so the chat is ready to do the work the user just asked for.
 */

import { useChatRail, type ChatScope } from "../chat/ChatRailContext";

export interface SparkleRegenProps {
  /** Agent node id. Required — the composition target. */
  agentId: string;
  /** Display name for the prompt template ("Sarah" → "Regenerate bio for Sarah …"). */
  agentName: string;
  /** Field key — drives the prompt language ("bio", "voice_tone", "motivations", …). */
  field: string;
  /** Optional persona style for nuance ("matter-of-fact", "playful", …). */
  style?: string | null;
  /** Render-prop callback wrapping the trigger so the parent can place it. */
  children: (onClick: () => void) => React.ReactNode;
}

/**
 * Pretty-print a snake-case field key for the prompt:
 *   "voice_tone" → "voice tone"
 *   "bio" → "bio"
 */
function humanField(field: string): string {
  return field.replace(/_/g, " ");
}

export function SparkleRegen({
  agentId,
  agentName,
  field,
  style,
  children,
}: SparkleRegenProps) {
  const chatRail = useChatRail();

  function onClick() {
    const scope: ChatScope = {
      kind: "agent",
      id: agentId,
      label: `Refining ${agentName}`,
    };
    const styleHint = style ? ` in ${style} voice` : "";
    const draft = `Regenerate ${humanField(field)} for ${agentName}${styleHint}.`;
    chatRail.openWith({
      scope,
      pageContext: {
        kind: "agent",
        id: agentId,
        name: agentName,
        snippet: `Refining ${field}`,
        fields: { regen_field: field },
      },
      draft,
      newChat: false,
    });
  }

  return <>{children(onClick)}</>;
}
